/**
 * <ac-bundle-products> — bundle add-on card for a product page.
 *
 * Responsibilities:
 *  1. Keep every companion product's variant in sync with the main product's
 *     selected size (the sheet set follows the mattress).
 *  2. When the toggle is on, add all companion lines to the cart together with
 *     the main product, so a cart-level bundle discount can match on 3 lines.
 *
 * The theme has no `variant:change` event (nothing dispatches it): the variant
 * picker re-renders `#shopify-section-<product-section-id>` over the network.
 * This section lives outside that markup, so it watches the picker itself and
 * re-reads the form's variant input once the swap lands.
 *
 * Several instances may share a page (different bundles, or a bundle plus an
 * upsell). Instances register with the module-level controller below, which
 * owns the single submit interceptor and merges the lines of every instance
 * whose toggle is on before handing the form back to the theme.
 */
(function () {
  // The section prints this tag once per instance; only the first run defines.
  if (customElements.get('ac-bundle-products')) return;

  var ADD_URL = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart/add.js';
  var SYNC_TRIES = 24;
  var SYNC_INTERVAL = 120;
  var PICKER_SELECTOR = 'variant-picker, [data-ac-bundle-variant-source]';

  // Hidden line item properties the discount function matches on.
  var PROP_BUNDLE = '_ac_bundle';
  var PROP_ROLE = '_ac_bundle_role';
  var ROLE_MAIN = 'main';
  var ROLE_ADDON = 'addon';

  // Published as data-bundle-surcharge (cents) plus this event so
  // assets/kik-product.js can fold the bundle into the buy button price.
  var SURCHARGE_EVENT = 'kik:bundle-surcharge';

  var instances = [];
  var listenersBound = false;

  function setFormProperty(form, key, value) {
    var name = 'properties[' + key + ']';
    var input = form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function removeFormProperty(form, key) {
    var input = form.querySelector('input[name="properties[' + key + ']"]');
    if (input) input.remove();
  }

  function addCommas(str) {
    return str.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
  }

  function formatMoney(cents, format) {
    var value = (Number(cents) || 0) / 100;
    var fmt = format || '${{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return addCommas(Math.round(value).toString());
        case 'amount_with_comma_separator':
          return addCommas(value.toFixed(2)).replace(/[.,]/g, function (char) {
            return char === '.' ? ',' : '.';
          });
        case 'amount_no_decimals_with_comma_separator':
          return addCommas(Math.round(value).toString()).replace(/,/g, '.');
        case 'amount_with_apostrophe_separator':
          return addCommas(value.toFixed(2)).replace(/,/g, "'");
        default:
          return addCommas(value.toFixed(2));
      }
    });
  }

  function normalizeSize(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/\s*\/\s*/g, ' / ')
      .replace(/\s+/g, ' ');
  }

  /**
   * A token in the variant map may be a variant id, a full variant title
   * ("Queen / 12 inch"), or just its size value ("Queen").
   */
  function tokenMatches(token, variant) {
    if (!token) return false;
    if (String(variant.id) === String(token).trim()) return true;
    var wanted = normalizeSize(token);
    return wanted === normalizeSize(variant.title) || wanted === normalizeSize(variant.size);
  }

  function findVariant(variants, token) {
    for (var i = 0; i < variants.length; i++) {
      if (tokenMatches(token, variants[i])) return variants[i];
    }
    return null;
  }

  /**
   * The variant map: one row per line, columns separated by `|`.
   *
   *   Queen / 12 inch | Standard | Queen
   *
   * Column 1 is the main product's variant; every column after it names the
   * variant to add for that bundle item, in order. A blank column falls through
   * to size matching. Lines starting with `#` are comments.
   */
  function parseVariantMap(raw) {
    var rows = [];
    if (!raw) return rows;
    String(raw)
      .split(/[\r\n]+/)
      .forEach(function (line) {
        var text = line.trim();
        if (!text || text.charAt(0) === '#') return;
        var columns = text.split('|').map(function (column) {
          return column.trim();
        });
        if (columns.length < 2 || !columns[0]) return;
        rows.push({ main: columns[0], items: columns.slice(1) });
      });
    return rows;
  }

  /** Pad columns so a pasted/printed table stays readable. */
  function formatTable(rows) {
    var widths = [];
    rows.forEach(function (row) {
      row.forEach(function (cell, i) {
        widths[i] = Math.max(widths[i] || 0, String(cell).length);
      });
    });
    return rows
      .map(function (row) {
        return row
          .map(function (cell, i) {
            cell = String(cell);
            return i === row.length - 1
              ? cell
              : cell + new Array(widths[i] - cell.length + 1).join(' ');
          })
          .join(' | ')
          .replace(/\s+$/, '');
      })
      .join('\n');
  }

  /** "Cal King=California King" per line, `=` or `:` separated. */
  function parseSizeMap(raw) {
    var map = {};
    if (!raw) return map;
    String(raw)
      .split(/[\r\n]+/)
      .forEach(function (line) {
        var parts = line.split(/[=:]/);
        if (parts.length < 2) return;
        var from = normalizeSize(parts[0]);
        var to = parts.slice(1).join('=').trim();
        if (from && to) map[from] = to;
      });
    return map;
  }

  /**
   * The picker swaps the product section asynchronously, so re-read the variant
   * input for a short while until every instance settles on the new value.
   */
  var resyncTimer = null;
  function scheduleResync() {
    if (resyncTimer) clearInterval(resyncTimer);
    var tries = 0;
    resyncTimer = setInterval(function () {
      tries += 1;
      instances.forEach(function (instance) {
        instance.sync();
      });
      if (tries >= SYNC_TRIES) {
        clearInterval(resyncTimer);
        resyncTimer = null;
      }
    }, SYNC_INTERVAL);
    instances.forEach(function (instance) {
      instance.sync();
    });
  }

  /**
   * The legacy kik-bundle-products block installs its own submit interceptor in
   * kik-component.js, which adds exactly one companion and re-submits the form.
   * Two interceptors on one form duplicate lines and race each other, so strip
   * the inputs the legacy one reads — without them it bails out before calling
   * preventDefault, leaving this module in charge. Re-run after every section
   * swap, which re-creates the legacy markup.
   */
  function disableLegacyBundleCart() {
    var legacy = document.querySelectorAll('bundle-products');
    if (!legacy.length) return;
    var removed = 0;
    for (var i = 0; i < legacy.length; i++) {
      var inputs = legacy[i].querySelectorAll(
        'input[name="default-product"], input[name="update-product"]'
      );
      for (var j = 0; j < inputs.length; j++) {
        inputs[j].remove();
        removed += 1;
      }
    }
    if (removed) {
      console.warn(
        '[ac-bundle-products] the legacy kik_bundle_products block is also on this page; ' +
          'disabled its add-to-cart hook (' +
          removed +
          ' inputs) so the two do not fight. Remove that block from the template.'
      );
    }
  }

  function onDocumentChange(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest(PICKER_SELECTOR)) return;
    scheduleResync();
  }

  function onDocumentSubmit(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

    var owners = instances.filter(function (instance) {
      return instance.ownsForm(form);
    });
    if (!owners.length) return;

    // Second pass after the companions landed: let the theme handle it.
    if (form.dataset.acBundleHandled === '1') {
      delete form.dataset.acBundleHandled;
      return;
    }

    var quantityInput = form.querySelector('[name="quantity"]');
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    if (!quantity || isNaN(quantity) || quantity < 1) quantity = 1;

    var items = [];
    var bundleKeys = [];
    owners.forEach(function (instance) {
      if (!instance.isEnabled()) return;
      var lines = instance.getLineItems(quantity);
      if (!lines.length) return;
      items = items.concat(lines);
      if (instance.config.bundleKey) bundleKeys.push(instance.config.bundleKey);
    });

    owners.forEach(function (instance) {
      instance.clearMainLineProperty(form);
    });

    if (!items.length) return;

    event.preventDefault();
    event.stopPropagation();

    if (bundleKeys.length) owners[0].setMainLineProperty(form, bundleKeys.join(','));
    var submitter = event.submitter || form.querySelector('[type="submit"][name="add"]');

    fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/add.js returned ' + response.status);
      })
      .catch(function (error) {
        // Still add the main product; the bundle discount just won't apply.
        console.warn('[ac-bundle-products] companion add failed', error);
      })
      .finally(function () {
        form.dataset.acBundleHandled = '1';
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter || undefined);
        } else if (submitter && typeof submitter.click === 'function') {
          submitter.click();
        } else {
          form.submit();
        }
      });
  }

  /**
   * The picker replaces every [data-product-block] with freshly rendered HTML,
   * and the form's variant input — the only place the new variant id shows up —
   * comes along with it. On a slow connection that lands after the resync
   * window has already closed, so restart the window when the markup appears.
   */
  function observeBlockSwaps() {
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(function (mutations) {
      var swapped = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return node.nodeType === 1 && node.hasAttribute('data-product-block');
        });
      });
      if (swapped) scheduleResync();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function bindListeners() {
    if (listenersBound) return;
    listenersBound = true;
    document.addEventListener('change', onDocumentChange, true);
    document.addEventListener('submit', onDocumentSubmit, true);
    observeBlockSwaps();
  }

  customElements.define(
    'ac-bundle-products',
    class ACBundleProducts extends HTMLElement {
      connectedCallback() {
        var configEl = this.querySelector('[data-ac-bundle-config]');
        if (!configEl) return;

        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (error) {
          console.warn('[ac-bundle-products] invalid config', error);
          return;
        }

        this.card = this.querySelector('[data-ac-bundle-card]');
        this.checkbox = this.querySelector('[data-ac-bundle-toggle]');
        this.compareEl = this.querySelector('[data-ac-bundle-compare]');
        this.saleEl = this.querySelector('[data-ac-bundle-sale]');
        this.noteEl = this.querySelector('[data-ac-bundle-note]');
        if (!this.checkbox) return;

        this.mainVariantIds = Object.keys(this.config.mainVariants || {});
        this.variantMap = parseVariantMap(this.config.variantMap);
        // Off until the real products are bound: adopt whatever product form the
        // page has, and fall back to a purchasable variant per item.
        this.strict = !!this.config.requireVariantMatch;

        // Each item carries its own config, so the same snippet works whether it
        // was rendered from section blocks or from a block's flat settings.
        this.items = [];
        var itemEls = this.querySelectorAll('[data-ac-bundle-item]');
        for (var i = 0; i < itemEls.length; i++) {
          var itemConfigEl = itemEls[i].querySelector('[data-ac-bundle-item-config]');
          if (!itemConfigEl) continue;
          var data;
          try {
            data = JSON.parse(itemConfigEl.textContent);
          } catch (error) {
            console.warn('[ac-bundle-products] invalid item config', error);
            continue;
          }
          this.items.push({
            data: data,
            sizeMap: parseSizeMap(data.sizeMap),
            el: itemEls[i],
            variant: null,
          });
        }
        if (!this.items.length) return;

        this.onToggle = this.onToggle.bind(this);
        this.checkbox.addEventListener('change', this.onToggle);

        if (instances.indexOf(this) === -1) instances.push(this);
        bindListeners();

        this.sync();
        this.validateConfiguration();

        // In the theme editor, show the generated map right under the card.
        if (window.Shopify && window.Shopify.designMode) {
          this.renderVariantMapHelper();
        }
      }

      disconnectedCallback() {
        if (this.checkbox) this.checkbox.removeEventListener('change', this.onToggle);
        var index = instances.indexOf(this);
        if (index !== -1) instances.splice(index, 1);
      }

      isEnabled() {
        return !!(this.checkbox && this.checkbox.checked && !this.checkbox.disabled);
      }

      /**
       * Catch the configuration mistakes that make this card look fine but do
       * nothing at add-to-cart time.
       */
      validateConfiguration() {
        if (!this.getMainForm()) {
          console.error(
            '[ac-bundle-products] found no add-to-cart form to attach to' +
              (this.strict
                ? ' for main product ' +
                  this.config.mainProductId +
                  '. Turn off "Require an exact variant match", or clear the "Main product" setting.'
                : ' on this page, so nothing will be added.')
          );
        }

        var itemCount = this.items.length;
        this.variantMap.forEach(function (row, index) {
          if (row.items.length > itemCount) {
            console.error(
              '[ac-bundle-products] variant map row ' +
                (index + 1) +
                ' ("' +
                row.main +
                '") has ' +
                row.items.length +
                ' item columns but this bundle has ' +
                itemCount +
                ' items. Every row needs one line of its own: ' +
                '"<main variant> | <item 1 variant> | <item 2 variant>".'
            );
          }
        });
      }

      onToggle() {
        if (this.card) this.card.classList.toggle('is-selected', this.checkbox.checked);
        this.updateSurcharge();
      }

      /**
       * What the buy button must add on top of the main variant price. The
       * discount covers the whole bundle, main product included, so this is the
       * discounted grand total minus the main price the button already shows —
       * which can be negative when the main product's share of the discount
       * outweighs the add-ons. Zero unless the bundle will actually reach the
       * cart, so the button always matches what is added.
       */
      updateSurcharge() {
        var cents = 0;

        if (this.checkbox && this.checkbox.checked && this.isComplete()) {
          var mainPrice = this.getMainPrice();
          var lines = [mainPrice];
          this.items.forEach(function (item) {
            if (!item.variant) return;
            lines.push(item.variant.price * (item.data.quantity || 1));
          });
          cents = this.applyBundleDiscount(lines) - mainPrice;
        }

        if (String(cents) === this.dataset.bundleSurcharge) return;
        this.dataset.bundleSurcharge = String(cents);
        document.dispatchEvent(new CustomEvent(SURCHARGE_EVENT));
      }

      /**
       * Discount a set of cart lines the way the cart itself does. A percentage
       * is applied line by line and only whole cents of each line's discount
       * are kept, so discounting the summed total instead loses up to a cent per
       * line — three lines is exactly the 3¢ the buy button used to be short.
       * A fixed amount comes off the bundle once, so there is nothing per-line
       * about it.
       */
      applyBundleDiscount(lineTotals) {
        var lines = Array.isArray(lineTotals) ? lineTotals : [lineTotals];
        var total = lines.reduce(function (sum, cents) {
          return sum + cents;
        }, 0);
        var value = Number(this.config.discountValue) || 0;

        if (this.config.discountType === 'percent') {
          return lines.reduce(function (sum, cents) {
            return sum + cents - Math.floor((cents * value) / 100);
          }, 0);
        }
        if (this.config.discountType === 'amount') return Math.max(0, total - Math.round(value * 100));
        return total;
      }

      getMainForm() {
        var forms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
        for (var i = 0; i < forms.length; i++) {
          if (this.ownsForm(forms[i])) return forms[i];
        }
        return null;
      }

      ownsForm(form) {
        var wrapper = form.closest('product-form');
        if (wrapper && wrapper.dataset.productId) {
          if (String(wrapper.dataset.productId) === String(this.config.mainProductId)) return true;
        }
        var idInput = form.querySelector('input[name="id"]');
        if (idInput && this.mainVariantIds.indexOf(String(idInput.value)) !== -1) return true;

        // Lenient mode adopts the page's own product form even when the
        // configured main product is something else, so the bundle still works
        // while products are still being wired up.
        return this.strict ? false : this.isPageProductForm(form);
      }

      /**
       * The add-to-cart form of the product this page is about — as opposed to a
       * related-product card or a quick-view, which must never be adopted.
       */
      isPageProductForm(form) {
        if (form.closest('quick-view')) return false;

        // Block layout: the card and the form share the product section.
        var section = this.closest('.shopify-section');
        if (section && section.contains(form)) return true;

        // Section layout: the form still has to live in the product info column.
        return !!form.closest('product-info');
      }

      getMainVariantId() {
        var form = this.getMainForm();
        var idInput = form && form.querySelector('input[name="id"]');
        if (idInput && idInput.value) return String(idInput.value);

        var fromUrl = new URLSearchParams(window.location.search).get('variant');
        if (fromUrl && this.config.mainVariants[fromUrl] !== undefined) return String(fromUrl);

        return String(this.config.mainVariantId || '');
      }

      getMainVariant() {
        var id = this.getMainVariantId();
        var entry = this.config.mainVariants[id] || {};
        return { id: id, size: entry.size || '', title: entry.title || '', price: Number(entry.price) || 0 };
      }

      /**
       * The main line's price in cents. Lenient mode can adopt a form that
       * belongs to another product, whose variants are not in the config map, so
       * fall back to whatever the picker currently reports — but only the picker
       * that belongs to the adopted form. Searching the whole document picks the
       * first picker in DOM order, which may be a quick-view or a sibling
       * product, and its price would silently skew the whole surcharge.
       */
      getMainPrice() {
        var fromConfig = this.getMainVariant().price;
        if (fromConfig) return fromConfig;

        var json = this.findMainPickerJson();
        if (!json) return 0;
        try {
          var variant = JSON.parse(json.textContent);
          return Number(variant && variant.price) || 0;
        } catch (e) {
          return 0;
        }
      }

      /**
       * The selected-variant JSON of the picker driving the form this instance is
       * attached to. Prefer the picker wired to the form by its `form` attribute;
       * otherwise stay inside the form's own product column / section.
       */
      findMainPickerJson() {
        var form = this.getMainForm();
        var scopes = [];

        if (form && form.id) {
          // Compare the attribute rather than build a selector out of the id:
          // section ids contain `--` and `__`, which a selector would have to
          // escape correctly to match.
          var pickers = document.querySelectorAll('variant-picker[form]');
          for (var p = 0; p < pickers.length; p++) {
            if (pickers[p].getAttribute('form') === form.id) {
              scopes.push(pickers[p]);
              break;
            }
          }
        }
        if (form) {
          scopes.push(form.closest('product-info'));
          scopes.push(form.closest('.shopify-section'));
        }
        scopes.push(this.closest('.shopify-section'));

        for (var i = 0; i < scopes.length; i++) {
          if (!scopes[i]) continue;
          var json = scopes[i].matches('variant-picker')
            ? scopes[i].querySelector('script[data-selected-variant-json]')
            : scopes[i].querySelector('variant-picker script[data-selected-variant-json]');
          if (json) return json;
        }
        return null;
      }

      /**
       * Build a ready-to-paste variant map out of the product data that is
       * already on the page: one row per real main-product variant, with each
       * item column pre-filled with the variant the automatic rules resolve to
       * (`???` where they resolve to nothing). Nobody should be typing variant
       * titles by hand.
       */
      buildVariantMapTemplate() {
        var self = this;
        var header = ['# main variant'].concat(
          this.items.map(function (item, index) {
            return 'item ' + (index + 1) + ' (product ' + item.data.productId + ')';
          })
        );

        var rows = [header];
        Object.keys(this.config.mainVariants || {}).forEach(function (id) {
          var entry = self.config.mainVariants[id] || {};
          var mainVariant = { id: id, size: entry.size || '', title: entry.title || '' };
          rows.push(
            [mainVariant.title || id].concat(
              self.items.map(function (item) {
                // Always strict here: `???` is the signal that the automatic
                // rules cannot resolve this cell and a human must fill it in.
                var resolved = self.resolveVariant(item, mainVariant, true, true);
                if (!resolved) return '???';
                return resolved.title + (resolved.available ? '' : ' (SOLD OUT)');
              })
            )
          );
        });

        return formatTable(rows);
      }

      /**
       * Check the configured variant map rather than ignore it: for every main
       * variant, what each item resolves to under strict rules. `???` marks a
       * gap the map does not cover — exactly what strict mode would refuse.
       */
      buildVariantMapReport() {
        var self = this;
        var rows = [
          ['# main variant', 'source'].concat(
            this.items.map(function (item, index) {
              return 'item ' + (index + 1);
            })
          ),
        ];

        Object.keys(this.config.mainVariants || {}).forEach(function (id) {
          var entry = self.config.mainVariants[id] || {};
          var mainVariant = { id: id, size: entry.size || '', title: entry.title || '' };
          var row = self.findMapRow(mainVariant);
          rows.push(
            [mainVariant.title || id, row ? 'map row "' + row.main + '"' : 'no map row'].concat(
              self.items.map(function (item, index) {
                var resolved = self.resolveVariant(item, mainVariant, false, true);
                if (!resolved) return '???';
                return resolved.title + (resolved.available ? '' : ' (SOLD OUT)');
              })
            )
          );
        });

        return formatTable(rows);
      }

      /** Theme-editor-only panel that surfaces the generated map for copying. */
      renderVariantMapHelper() {
        var panel = document.createElement('details');
        panel.setAttribute('style', 'margin-top:12px;font:12px/1.5 ui-monospace,monospace;color:#333');
        var summary = document.createElement('summary');
        summary.setAttribute('style', 'cursor:pointer;font-weight:600');
        summary.textContent = 'Variant map — generated from this product\'s variants (copy into the setting)';
        var box = document.createElement('textarea');
        box.readOnly = true;
        box.setAttribute('rows', String(Math.min(20, Object.keys(this.config.mainVariants || {}).length + 2)));
        box.setAttribute(
          'style',
          'width:100%;margin-top:8px;font:12px/1.5 ui-monospace,monospace;white-space:pre;overflow-x:auto'
        );
        box.value = this.buildVariantMapTemplate();
        panel.appendChild(summary);
        panel.appendChild(box);
        this.appendChild(panel);
      }

      /** The variant map row that covers the currently selected main variant. */
      findMapRow(mainVariant) {
        for (var i = 0; i < this.variantMap.length; i++) {
          if (tokenMatches(this.variantMap[i].main, mainVariant)) return this.variantMap[i];
        }
        return null;
      }

      /**
       * Resolve one bundle item's variant, most specific rule first:
       *   1. the variant map — an explicit per-main-variant column
       *   2. size matching against the main product's size
       *   3. the item's own default variant
       *
       * `ignoreMap` skips step 1; the map template generator uses it to show
       * what the automatic rules would pick.
       */
      resolveVariant(item, mainVariant, ignoreMap, forceStrict) {
        var strict = forceStrict === undefined ? this.strict : forceStrict;
        var variants = item.data.variants || [];
        if (!variants.length) return null;

        var row = ignoreMap ? null : this.findMapRow(mainVariant);
        if (row) {
          var token = row.items[item.data.index];
          if (token) {
            var mapped = findVariant(variants, token);
            if (mapped) return mapped;
            // A row naming a variant that doesn't exist is a configuration
            // error. Strict mode fails loudly instead of adding the wrong one.
            return strict ? null : this.fallbackVariant(item);
          }
        }

        if (item.data.matchSize) {
          var matched = null;
          if (mainVariant.size) {
            var wanted = normalizeSize(
              item.sizeMap[normalizeSize(mainVariant.size)] || mainVariant.size
            );
            matched = variants.filter(function (variant) {
              return normalizeSize(variant.size) === wanted;
            })[0] || null;
          }
          if (matched) return matched;
          return strict ? null : this.fallbackVariant(item);
        }

        // A fixed item: nothing to match against, so its own configured variant
        // is the answer in either mode.
        var configured = this.configuredVariant(item);
        if (strict) return configured || variants[0] || null;
        return this.fallbackVariant(item);
      }

      /** The variant the merchant picked for this item. */
      configuredVariant(item) {
        return (
          (item.data.variants || []).filter(function (variant) {
            return variant.id === item.data.defaultVariantId;
          })[0] || null
        );
      }

      /**
       * Lenient fallback: the item's configured variant, or any purchasable one.
       * Keeps the bundle working before the real product/variant relationships
       * are bound; strict mode never substitutes.
       */
      fallbackVariant(item) {
        var variants = item.data.variants || [];
        var preferred = this.configuredVariant(item);
        if (preferred && preferred.available) return preferred;
        return (
          variants.filter(function (variant) {
            return variant.available;
          })[0] || preferred || variants[0] || null
        );
      }

      sync() {
        disableLegacyBundleCart();

        var mainVariant = this.getMainVariant();
        // Keyed on the variant, not the size: two variants can share a size and
        // still map to different bundle items.
        if (this.hasSynced && this.lastSyncedVariantId === mainVariant.id) {
          // 主价格可能是从 picker 的 HTML 兜底读来的，而那段 HTML 要等 block 换完
          // 才是新值，所以每次 resync 都把加价重算一遍。值没变时不会派发事件，
          // 白算一次的代价只是几个加法。
          this.updateSurcharge();
          return;
        }
        this.lastSyncedVariantId = mainVariant.id;
        this.hasSynced = true;

        var unresolved = 0;

        this.items.forEach(function (item) {
          var previous = item.variant;
          var resolved = this.resolveVariant(item, mainVariant);
          item.variant = resolved && resolved.available ? resolved : null;
          if (!item.variant) {
            unresolved += 1;
            this.warnUnresolved(item, mainVariant, resolved);
          }
          if (!item.el) return;

          item.el.classList.toggle('is-unavailable', !item.variant);

          var display = resolved || previous;
          if (!display) return;

          // The image comes from `image-skeleton`, so it may be a lazysizes
          // element: pin src and drop the srcset pair so it stops being managed.
          var media = item.el.querySelector('[data-ac-bundle-media]');
          var image = media && media.querySelector('img');
          if (image && display.image && !item.data.imageOverride && image.getAttribute('src') !== display.image) {
            image.setAttribute('src', display.image);
            image.removeAttribute('srcset');
            image.removeAttribute('data-srcset');
          }

          var sizeEl = item.el.querySelector('[data-ac-bundle-item-size]');
          if (sizeEl) sizeEl.textContent = item.data.matchSize && display.size ? display.size : '';

          var valueEl = item.el.querySelector('[data-ac-bundle-item-value]');
          if (valueEl && !item.data.compValueOverride) {
            var comp = display.compareAtPrice || display.price;
            valueEl.innerHTML =
              '<s>' + formatMoney(comp, this.config.moneyFormat) + '</s> ' + (this.config.compLabel || '');
          }
        }, this);

        if (this.noteEl) this.noteEl.hidden = unresolved === 0;

        if (unresolved > 0 && this.config.disableWhenUnavailable) {
          this.checkbox.checked = false;
          this.checkbox.disabled = true;
          this.onToggle();
        } else {
          this.checkbox.disabled = false;
        }

        this.renderPrices();
        this.updateSurcharge();
      }

      renderPrices() {
        if (this.config.priceMode !== 'auto') return;

        var compare = 0;
        var lines = [];
        this.items.forEach(function (item) {
          if (!item.variant) return;
          var quantity = item.data.quantity || 1;
          compare += (item.variant.compareAtPrice || item.variant.price) * quantity;
          lines.push(item.variant.price * quantity);
        });

        var sale = this.applyBundleDiscount(lines);

        if (this.compareEl) {
          this.compareEl.textContent = compare > sale ? formatMoney(compare, this.config.moneyFormat) : '';
        }
        if (this.saleEl) {
          this.saleEl.textContent = sale > 0 ? formatMoney(sale, this.config.moneyFormat) : '';
        }
      }

      /**
       * True when every item resolved to a purchasable variant. A partial
       * bundle must never reach the cart: the customer would pay full price for
       * the wrong set of products, because the discount function only fires on
       * a complete bundle.
       */
      isComplete() {
        if (!this.items.length) return false;
        for (var i = 0; i < this.items.length; i++) {
          if (!this.items[i].variant) return false;
        }
        return true;
      }

      /**
       * Explain a failed match in the console, naming what was looked for and
       * what the product actually offers.
       */
      warnUnresolved(item, mainVariant, resolved) {
        var row = this.findMapRow(mainVariant);
        var token = row && row.items[item.data.index];
        var offered = (item.data.variants || [])
          .map(function (variant) {
            return variant.title + (variant.available ? '' : ' (sold out)');
          })
          .join(', ');
        console.warn(
          '[ac-bundle-products] item ' +
            item.data.index +
            ' (product ' +
            item.data.productId +
            ') has no usable variant for main variant "' +
            (mainVariant.title || mainVariant.id) +
            '". ' +
            (token
              ? 'Variant map column asked for "' + token + '"' +
                (resolved ? ', which is sold out' : ', which does not exist') + '. '
              : row
                ? 'Variant map row matched but its column for this item is blank, and '
                : 'No variant map row matched, and ') +
            (token ? '' : 'size matching found ' + (resolved ? 'a sold-out variant. ' : 'nothing. ')) +
            'Available: ' +
            offered
        );
      }

      getLineItems(quantity) {
        var bundleKey = this.config.bundleKey;
        if (!this.isComplete()) return [];
        return this.items
          .filter(function (item) {
            return !!item.variant;
          })
          .map(function (item) {
            var line = {
              id: item.variant.id,
              quantity: (item.data.quantity || 1) * quantity,
            };
            if (bundleKey) {
              line.properties = { _ac_bundle: bundleKey, _ac_bundle_role: ROLE_ADDON };
            }
            return line;
          });
      }

      /**
       * Tag the main line so the discount function can pair it with the add-ons.
       * The function needs both the key (which bundle) and the role (which line
       * keeps its price and which ones get discounted).
       */
      setMainLineProperty(form, value) {
        setFormProperty(form, PROP_BUNDLE, value);
        setFormProperty(form, PROP_ROLE, ROLE_MAIN);
      }

      clearMainLineProperty(form) {
        removeFormProperty(form, PROP_BUNDLE);
        removeFormProperty(form, PROP_ROLE);
      }
    }
  );

  /**
   * Console helper: dumps why a bundle card is (or is not) going to add lines.
   *
   *   acBundleDebug()
   */
  window.acBundleDebug = function () {
    if (!instances.length) {
      console.log('[ac-bundle-products] no bundle card on this page');
      return [];
    }
    var report = instances.map(function (instance) {
      var mainVariant = instance.getMainVariant();
      var form = instance.getMainForm();
      var wrapper = form && form.closest('product-form');
      return {
        bundleKey: instance.config.bundleKey,
        strictVariantMatch: !!instance.strict,
        mainProductId: instance.config.mainProductId,
        ownsAForm: !!form,
        adoptedPageForm: !!form && String(instance.config.mainProductId) !==
          String(form.closest('product-form') && form.closest('product-form').dataset.productId),
        formProductId: wrapper ? wrapper.dataset.productId : null,
        mainVariant: mainVariant,
        toggleChecked: !!(instance.checkbox && instance.checkbox.checked),
        toggleDisabled: !!(instance.checkbox && instance.checkbox.disabled),
        variantMapRows: instance.variantMap.length,
        matchedRow: instance.findMapRow(mainVariant),
        complete: instance.isComplete(),
        items: instance.items.map(function (item) {
          return {
            index: item.data.index,
            productId: item.data.productId,
            matchSize: !!item.data.matchSize,
            resolved: item.variant ? item.variant.title + ' (' + item.variant.id + ')' : null,
          };
        }),
        wouldAdd: instance.getLineItems(1),
        legacyBlockPresent: !!document.querySelector('bundle-products'),
      };
    });
    console.log(report);
    return report;
  };

  /**
   * Console helper for a live product page: prints a ready-to-paste variant map
   * for every bundle card present, built from the real variant data.
   *
   *   acBundleVariantMap()
   */
  /**
   * Console helper: verify the configured variant map. Prints, for every main
   * variant, which map row matched and what each item resolves to under strict
   * rules — `???` is a size the map fails to cover.
   *
   *   acBundleCheckMap()
   */
  window.acBundleCheckMap = function () {
    if (!instances.length) {
      console.log('[ac-bundle-products] no bundle card on this page');
      return '';
    }
    var output = instances
      .map(function (instance) {
        return '--- ' + instance.config.bundleKey + ' ---\n' + instance.buildVariantMapReport();
      })
      .join('\n\n');
    console.log(output);
    return output;
  };

  window.acBundleVariantMap = function () {
    if (!instances.length) {
      console.log('[ac-bundle-products] no bundle card on this page');
      return '';
    }
    var output = instances
      .map(function (instance) {
        return '--- ' + instance.config.bundleKey + ' ---\n' + instance.buildVariantMapTemplate();
      })
      .join('\n\n');
    console.log(output);
    return output;
  };
})();
